import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import dayjs from 'dayjs';
import { connectorsApi } from '../../api/connectors';

/** Kobra IA — alineado con Seguimiento */
const K = {
  navy: '#121826',
  blue: '#4185F1',
  green: '#6BD56F',
  border: 'rgba(18, 24, 38, 0.08)',
  surface: '#F4F6F9',
};
const kobraShell = {
  borderRadius: 2,
  boxShadow: '0 4px 24px rgba(18, 24, 38, 0.07)',
  border: `1px solid ${K.border}`,
  bgcolor: '#fff',
};

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => CURRENCY_FORMATTER.format(value || 0);

const formatDateLabel = (dateStr) => {
  if (!dateStr) return '';
  const d = dayjs(dateStr);
  return d.isValid() ? d.format('MMM D') : dateStr;
};

const CollectionsImpactTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  const totalCollected = payload.find((p) => p.dataKey === 'total_collected')?.value ?? 0;
  const botCollected = payload.find((p) => p.dataKey === 'bot_collected')?.value ?? 0;

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: '#fff',
        boxShadow: `0 4px 20px ${alpha(K.navy, 0.08)}`,
        border: `1px solid ${K.border}`,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700, color: K.navy }}>
        {dayjs(label).format('DD/MM/YYYY')}
      </Typography>
      <Typography variant="body2" sx={{ color: K.blue, fontWeight: 600 }}>
        Total cobrado: {formatCurrency(totalCollected)}
      </Typography>
      <Typography variant="body2" sx={{ color: K.green, fontWeight: 600 }}>
        Bot: {formatCurrency(botCollected)}
      </Typography>
    </Box>
  );
};

export const CollectionsImpactChart = () => {
  const [periodAFrom, setPeriodAFrom] = useState(dayjs().subtract(1, 'month').startOf('day'));
  const [periodATo, setPeriodATo] = useState(dayjs().endOf('day'));

  const [periodBFrom, setPeriodBFrom] = useState(dayjs().subtract(2, 'month').startOf('day'));
  const [periodBTo, setPeriodBTo] = useState(dayjs().subtract(1, 'month').endOf('day'));

  // What range to show in the line chart
  const [chartPeriod, setChartPeriod] = useState('A');

  const [dataA, setDataA] = useState([]);
  const [dataB, setDataB] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [error, setError] = useState(null);

  const daysInRange = (from, to) => {
    if (!from || !to) return 0;
    const start = from.startOf('day');
    const end = to.startOf('day');
    if (end.isBefore(start, 'day')) return 0;
    return end.diff(start, 'day') + 1;
  };

  const toServerDate = (d) => (d ? d.startOf('day').format('YYYY-MM-DD') : null);

  const formatRange = (from, to) => {
    if (!from || !to) return 'N/A';
    return `${from.format('DD/MM/YYYY')} - ${to.format('DD/MM/YYYY')}`;
  };

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const startDateA = toServerDate(periodAFrom);
      const endDateA = toServerDate(periodATo);
      const startDateB = toServerDate(periodBFrom);
      const endDateB = toServerDate(periodBTo);

      // Obtener conectores y encontrar el configurado para historial de pagos
      const connectors = await connectorsApi.getConnectors();
      const connector = connectors[0]

      if (!connector) {
        throw new Error(
          'No hay un conector configurado para "obtener_historial_pagos". Configúralo en la sección de Conectores.'
        );
      }

   
      const baseUrl = (connector.baseUrl || '').replace(/\/+$/, '');
      const path = "/historial_resumen_pagos"

      const url = `${baseUrl}${path}`;

      const postSummary = async (s, e) => {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            start_date: s,
            end_date: e,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => '');
          throw new Error(
            `Error al cargar impacto de cobranzas (${response.status}): ${errorText.slice(
              0,
              160
            )}`
          );
        }

        const result = await response.json();
        return Array.isArray(result) ? result : [];
      };

      const [normalizedA, normalizedB] = await Promise.all([
        postSummary(startDateA, endDateA),
        postSummary(startDateB, endDateB),
      ]);

      setDataA(normalizedA);
      setDataB(normalizedB);
      setIsEmpty(normalizedA.length === 0 && normalizedB.length === 0);
    } catch (err) {
      setError(err.message || 'Unexpected error');
      setDataA([]);
      setDataB([]);
      setIsEmpty(true);
    } finally {
      setIsLoading(false);
    }
  }, [periodAFrom, periodATo, periodBFrom, periodBTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const {
    avgDailyCollectedA,
    avgDailyCollectedB,
    avgDailyChangePct,
  } = useMemo(() => {
    const base = {
      avgDailyCollectedA: 0,
      avgDailyCollectedB: 0,
      avgDailyChangePct: null,
    };

    const sumTotals = (rows) =>
      (rows || []).reduce((acc, item) => acc + (item.total_collected || 0), 0);

    const daysA = daysInRange(periodAFrom, periodATo);
    const daysB = daysInRange(periodBFrom, periodBTo);

    const totalA = sumTotals(dataA);
    const totalB = sumTotals(dataB);

    const avgA = daysA > 0 ? totalA / daysA : 0;
    
    const avgB = daysB > 0 ? totalB / daysB : 0;

    const calcChange = (current, previous) => {
      if (previous === 0) return null;
      return ((current - previous) / previous) * 100;
    };

    return {
      avgDailyCollectedA: avgA,
      avgDailyCollectedB: avgB,
      avgDailyChangePct: calcChange(avgB, avgA),
    };
  }, [dataA, dataB, periodAFrom, periodATo, periodBFrom, periodBTo]);

  const chartData = chartPeriod === 'A' ? dataA : dataB;

  const renderSkeleton = () => (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[0, 1, 2].map((key) => (
          <Grid key={key} item size={{ xs: 12, md: 4 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Skeleton variant="text" width={120} />
              <Skeleton variant="text" width={80} height={32} />
            </Box>
          </Grid>
        ))}
      </Grid>
      <Skeleton variant="rectangular" height={260} sx={{ borderRadius: 2 }} />
    </Box>
  );

  const renderEmptyState = () => (
    <Box
      sx={{
        py: 6,
        px: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: K.navy }}>
        No hay datos para este período
      </Typography>
      <Typography variant="body2" sx={{ color: alpha(K.navy, 0.55), maxWidth: 360 }}>
        Ajusta el rango de fechas o vuelve a intentarlo más tarde cuando haya más información de cobranzas.
      </Typography>
    </Box>
  );

  return (
    <Card sx={{ ...kobraShell, height: '100%' }}>
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Box
          sx={{
            mb: 2.5,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: K.navy, letterSpacing: '-0.02em' }}>
              Impacto de cobranzas
            </Typography>
            <Typography variant="body2" sx={{ color: alpha(K.navy, 0.55), mt: 0.25 }}>
              Cobranzas diarias y contribución del bot de automatización
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={chartPeriod}
            exclusive
            onChange={(_, value) => {
              if (value) setChartPeriod(value);
            }}
            size="small"
            sx={{
              backgroundColor: K.surface,
              borderRadius: 999,
              border: `1px solid ${K.border}`,
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                px: 1.8,
                fontWeight: 600,
                border: 'none',
                color: alpha(K.navy, 0.65),
                '&.Mui-selected': {
                  backgroundColor: K.blue,
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#3574d9',
                  },
                },
              },
            }}
          >
            <ToggleButton value="A">Período A</ToggleButton>
            <ToggleButton value="B">Período B</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: K.navy, fontSize: '0.75rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Período A
              </Typography>
              <Box sx={{ mb: 1 }}>
                <DatePicker
                  value={periodAFrom}
                  onChange={(newValue) => setPeriodAFrom(newValue)}
                  format="DD/MM/YYYY"
                  label="Fecha Desde"
                  maxDate={periodATo}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          bgcolor: '#fff',
                          '& fieldset': { borderColor: 'rgba(18, 24, 38, 0.1)' },
                        },
                      },
                    },
                  }}
                />
              </Box>
              <DatePicker
                value={periodATo}
                onChange={(newValue) => setPeriodATo(newValue)}
                format="DD/MM/YYYY"
                label="Fecha Hasta"
                minDate={periodAFrom}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        bgcolor: '#fff',
                        '& fieldset': { borderColor: 'rgba(18, 24, 38, 0.1)' },
                      },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: K.navy, fontSize: '0.75rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Período B
              </Typography>
              <Box sx={{ mb: 1 }}>
                <DatePicker
                  value={periodBFrom}
                  onChange={(newValue) => setPeriodBFrom(newValue)}
                  format="DD/MM/YYYY"
                  label="Fecha Desde"
                  maxDate={periodBTo}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          bgcolor: '#fff',
                          '& fieldset': { borderColor: 'rgba(18, 24, 38, 0.1)' },
                        },
                      },
                    },
                  }}
                />
              </Box>
              <DatePicker
                value={periodBTo}
                onChange={(newValue) => setPeriodBTo(newValue)}
                format="DD/MM/YYYY"
                label="Fecha Hasta"
                minDate={periodBFrom}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        bgcolor: '#fff',
                        '& fieldset': { borderColor: 'rgba(18, 24, 38, 0.1)' },
                      },
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>

        {isLoading ? (
          renderSkeleton()
        ) : isEmpty ? (
          renderEmptyState()
        ) : (
          <>
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item size={{ xs: 12, md: 4 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${K.border}`,
                    borderTop: `3px solid ${K.blue}`,
                    bgcolor: alpha(K.blue, 0.04),
                  }}
                >
                  <Typography variant="caption" sx={{ color: alpha(K.navy, 0.55), fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                    Promedio cobrado diario (Período A)
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: K.navy, mt: 0.5 }}>
                    {formatCurrency(avgDailyCollectedA)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: alpha(K.navy, 0.45), mt: 0.5, display: 'block' }}>
                    {formatRange(periodAFrom, periodATo)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${K.border}`,
                    borderTop: `3px solid ${K.green}`,
                    bgcolor: alpha(K.green, 0.06),
                  }}
                >
                  <Typography variant="caption" sx={{ color: alpha(K.navy, 0.55), fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                    Promedio cobrado diario (Período B)
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: K.navy, mt: 0.5 }}>
                    {formatCurrency(avgDailyCollectedB)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: alpha(K.navy, 0.45), mt: 0.5, display: 'block' }}>
                    {formatRange(periodBFrom, periodBTo)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item size={{ xs: 12, md: 4 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${K.border}`,
                    borderTop: `3px solid ${alpha(K.navy, 0.35)}`,
                    bgcolor: alpha(K.navy, 0.03),
                  }}
                >
                  <Typography variant="caption" sx={{ color: alpha(K.navy, 0.55), fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                    Cambio % (B vs A)
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                      mt: 0.5,
                      color:
                        avgDailyChangePct === null
                          ? K.navy
                          : avgDailyChangePct >= 0
                            ? '#2E7D32'
                            : '#C62828',
                    }}
                  >
                    {avgDailyChangePct === null
                      ? 'N/A'
                      : `${avgDailyChangePct >= 0 ? '+' : ''}${avgDailyChangePct.toFixed(1)}%`}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(K.navy, 0.08)} vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDateLabel}
                    tick={{ fontSize: 12, fill: alpha(K.navy, 0.55) }}
                    axisLine={{ stroke: K.border }}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                    tick={{ fontSize: 12, fill: alpha(K.navy, 0.55) }}
                    axisLine={{ stroke: K.border }}
                    tickLine={false}
                  />
                  <Tooltip content={<CollectionsImpactTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="total_collected"
                    stroke={K.blue}
                    strokeWidth={2.2}
                    dot={false}
                    activeDot={{ r: 4, fill: K.blue }}
                    name="Total Collected"
                  />
{/*                  <Line
                    type="monotone"
                    dataKey="bot_collected"
                    stroke="#2e7d32"
                    strokeWidth={2.2}
                    dot={false}
                    activeDot={{ r: 4 }}
                    name="Bot Collected"
                  />
*/}                </LineChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}

        {error && !isLoading && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: '#C62828', fontWeight: 600 }}>
              {error}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CollectionsImpactChart;

